import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Plus, Search, User, UserPlus, ChevronLeft, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useClient, useClientsInfinite } from '@/lib/queries';

interface Props {
  value: string;
  onChange: (id: string) => void;
  onCreated?: (c: any) => void;
  /**
   * Notifies the parent form when the picker sheet is open. The parent should
   * hide its own submit button (e.g. Telegram MainButton) so the user is not
   * confused by an "outer" submit while filling the inner "new client" form.
   */
  onOpenChange?: (open: boolean) => void;
}

type PinnedClient = { id: string; name: string; phone?: string | null };

export const ClientPicker = ({ value, onChange, onCreated, onOpenChange }: Props) => {
  const [open, setOpenState] = useState(false);
  const setOpen = (v: boolean) => {
    setOpenState(v);
    onOpenChange?.(v);
    if (!v) {
      setCreating(false);
      setName('');
      setPhone('');
      setCreatingError(null);
      clearPhoto();
      setQ('');
    }
  };

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data: listPages,
    isLoading: listLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useClientsInfinite({
    q: debouncedQ || undefined,
    sort: 'name',
  });

  const listItems = useMemo(() => {
    const pages = listPages?.pages ?? [];
    const seen = new Set<string>();
    const merged: any[] = [];
    for (const page of pages) {
      for (const c of page.items) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        merged.push(c);
      }
    }
    return merged;
  }, [listPages]);

  const total = listPages?.pages[0]?.total ?? listItems.length;

  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open || creating) return;
    const root = scrollRef.current;
    const target = loadMoreRef.current;
    if (!root || !target) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, rootMargin: '100px' },
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [open, creating, hasNextPage, isFetchingNextPage, fetchNextPage, listItems.length]);

  const [pinned, setPinned] = useState<PinnedClient | null>(null);
  const { data: fetchedClient } = useClient(
    value && !listItems.some((c: any) => c.id === value) && pinned?.id !== value ? value : undefined,
  );

  useEffect(() => {
    if (!value) {
      setPinned(null);
      return;
    }
    const inList = listItems.find((c: any) => c.id === value);
    if (inList) {
      setPinned({ id: inList.id, name: inList.name, phone: inList.phone });
    } else if (fetchedClient?.id === value) {
      setPinned({ id: fetchedClient.id, name: fetchedClient.name, phone: fetchedClient.phone });
    }
  }, [value, listItems, fetchedClient]);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const pickPhoto = (file: File | null) => {
    if (!file) return;
    if (file.type && !file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') setPhotoPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const displayLabel = useMemo(() => {
    if (!value) return null;
    if (pinned?.id === value) return pinned.name;
    const inList = listItems.find((c: any) => c.id === value);
    if (inList) return inList.name;
    if (fetchedClient?.id === value) return fetchedClient.name;
    return null;
  }, [value, pinned, listItems, fetchedClient]);

  const qc = useQueryClient();
  const [creatingError, setCreatingError] = useState<string | null>(null);
  const [submittingNew, setSubmittingNew] = useState(false);

  const selectClient = (c: { id: string; name: string; phone?: string | null }) => {
    setPinned({ id: c.id, name: c.name, phone: c.phone });
    onChange(c.id);
    setOpen(false);
  };

  const create = async () => {
    if (!name.trim() || submittingNew) return;
    setCreatingError(null);
    setSubmittingNew(true);
    try {
      const { data } = await api.post('/clients', { name: name.trim(), phone: phone || undefined });
      let photoError: string | null = null;
      if (photoFile) {
        const safe =
          photoFile.type && photoFile.type.startsWith('image/')
            ? photoFile
            : new File([photoFile], photoFile.name || 'photo.jpg', {
                type: 'image/jpeg',
              });
        const fd = new FormData();
        fd.append('file', safe);
        try {
          await api.post(`/clients/${data.id}/photos`, fd);
        } catch (err: any) {
          photoError =
            'Клиент создан, но фото загрузить не удалось: ' +
            (err?.response?.data?.error
              ? typeof err.response.data.error === 'string'
                ? err.response.data.error
                : 'ошибка сервера'
              : err?.message ?? 'неизвестная ошибка');
        }
      }
      await qc.invalidateQueries({ queryKey: ['clients'], refetchType: 'all' });
      setPinned({ id: data.id, name: data.name, phone: data.phone });
      onChange(data.id);
      onCreated?.(data);
      if (photoError) {
        setCreatingError(photoError);
      } else {
        setOpen(false);
      }
    } catch (e: any) {
      const raw = e?.response?.data?.error;
      let msg = 'Не удалось создать клиента';
      if (typeof raw === 'string') {
        msg = raw;
      } else if (raw && typeof raw === 'object') {
        const fields = (raw as any).fieldErrors ?? {};
        const first = Object.entries(fields).find(([, v]) => Array.isArray(v) && v.length);
        if (first) {
          const labelByField: Record<string, string> = {
            name: 'Имя',
            phone: 'Телефон',
            telegram_username: 'Telegram',
          };
          const label = labelByField[first[0]] ?? first[0];
          const message = (first[1] as string[])[0];
          msg = `${label}: ${message}`;
        } else if ((raw as any).formErrors?.length) {
          msg = (raw as any).formErrors.join(', ');
        }
      }
      setCreatingError(msg);
    } finally {
      setSubmittingNew(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex h-11 w-full items-center justify-between rounded-xl border bg-background px-3 text-base"
        >
          <span className={displayLabel ? '' : 'text-muted-foreground'}>
            {displayLabel ?? 'Выберите клиента'}
          </span>
          <User className="h-4 w-4 text-muted-foreground" />
        </button>
      </SheetTrigger>
      <SheetContent className="flex max-h-[92vh] flex-col">
        <SheetHeader>
          <SheetTitle>{creating ? 'Новый клиент' : 'Выбор клиента'}</SheetTitle>
        </SheetHeader>

        {creating ? (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-3">
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="-ml-1 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" /> К списку
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-border bg-muted/40 text-muted-foreground hover:border-primary hover:text-primary"
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                </button>
                <div className="text-xs text-muted-foreground">
                  {photoPreview ? (
                    <button
                      type="button"
                      onClick={clearPhoto}
                      className="inline-flex items-center gap-1 text-destructive"
                    >
                      <X className="h-3 w-3" /> Убрать фото
                    </button>
                  ) : (
                    'Фото — необязательно'
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    pickPhoto(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
              </div>
              <div className="space-y-2">
                <Input
                  placeholder="Имя"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
                <Input
                  placeholder="Телефон"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              {creatingError && (
                <div className="text-xs text-destructive">{creatingError}</div>
              )}
              <p className="text-xs text-muted-foreground">
                Клиент будет создан и сразу выбран в текущей записи.
              </p>
            </div>
            <div className="sticky bottom-0 -mx-4 border-t bg-card px-4 pb-2 pt-3">
              <Button
                onClick={create}
                disabled={!name.trim() || submittingNew}
                size="lg"
                className="h-12 w-full text-base"
              >
                <UserPlus className="h-4 w-4" />
                {submittingNew ? 'Создание…' : 'Создать клиента'}
              </Button>
            </div>
          </>
        ) : (
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Поиск по имени или телефону…"
                className="pl-9"
              />
            </div>
            <Button variant="outline" className="w-full" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Новый клиент
            </Button>
            {listLoading && (
              <div className="text-center text-xs text-muted-foreground">Загрузка…</div>
            )}
            {isFetching && !listLoading && !isFetchingNextPage && (
              <div className="text-center text-xs text-muted-foreground">Поиск…</div>
            )}
            <div className="space-y-1">
              {listItems.map((c: any) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectClient(c)}
                  className={`flex w-full items-center justify-between rounded-lg p-3 text-left hover:bg-accent ${
                    value === c.id ? 'bg-primary/10 ring-1 ring-primary/30' : ''
                  }`}
                >
                  <div>
                    <div className="font-medium">{c.name}</div>
                    {c.phone && (
                      <div className="text-xs text-muted-foreground">{c.phone}</div>
                    )}
                  </div>
                  {c.stats?.visits_count > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {c.stats.visits_count} виз.
                    </span>
                  )}
                </button>
              ))}
              {!listLoading && !isFetching && !listItems.length && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {debouncedQ ? 'Никого не найдено' : 'Клиенты не найдены'}
                </div>
              )}
              <div ref={loadMoreRef} className="h-1 w-full shrink-0" aria-hidden />
              {isFetchingNextPage && (
                <p className="py-2 text-center text-xs text-muted-foreground">Загрузка…</p>
              )}
              {hasNextPage && listItems.length > 0 && (
                <p className="px-2 py-1 text-center text-xs text-muted-foreground">
                  Показано {listItems.length} из {total} · листайте вниз
                </p>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
